import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// Date formatting utilities
export function formatDate(date: string | Date, format: 'short' | 'medium' | 'long' | 'time' = 'medium'): string {
  const d = new Date(date)
  
  if (isNaN(d.getTime())) {
    return 'Invalid date'
  }

  const now = new Date()
  const diff = now.getTime() - d.getTime()
  const days = Math.floor(diff / (1000 * 60 * 60 * 24))
  const hours = Math.floor(diff / (1000 * 60 * 60))
  const minutes = Math.floor(diff / (1000 * 60))

  // Relative time for recent dates
  if (format === 'short') {
    if (minutes < 1) return 'Just now'
    if (minutes < 60) return `${minutes}m ago`
    if (hours < 24) return `${hours}h ago`
    if (days < 7) return `${days}d ago`
  }

  const optionsMap: Record<string, Intl.DateTimeFormatOptions> = {
    short: { month: 'short', day: 'numeric' },
    medium: { month: 'short', day: 'numeric', year: 'numeric' },
    long: { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' },
    time: { hour: 'numeric', minute: '2-digit', hour12: true }
  }
  const options = optionsMap[format]

  return d.toLocaleDateString('en-US', options)
}

export function formatDateTime(date: string | Date): string {
  const d = new Date(date)
  return d.toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric', 
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  })
}

export function formatRelativeTime(date: string | Date): string {
  const d = new Date(date)
  const now = new Date()
  const diff = now.getTime() - d.getTime()
  
  const minutes = Math.floor(diff / (1000 * 60))
  const hours = Math.floor(diff / (1000 * 60 * 60))
  const days = Math.floor(diff / (1000 * 60 * 60 * 24))
  const weeks = Math.floor(days / 7)
  const months = Math.floor(days / 30)
  const years = Math.floor(days / 365)

  if (minutes < 1) return 'Just now'
  if (minutes < 60) return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`
  if (hours < 24) return `${hours} hour${hours !== 1 ? 's' : ''} ago`
  if (days < 7) return `${days} day${days !== 1 ? 's' : ''} ago`
  if (weeks < 4) return `${weeks} week${weeks !== 1 ? 's' : ''} ago`
  if (months < 12) return `${months} month${months !== 1 ? 's' : ''} ago`
  return `${years} year${years !== 1 ? 's' : ''} ago`
}

// Currency formatting
export function formatCurrency(amount: number, currency = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount)
}

// Number formatting
export function formatNumber(value: number, decimals = 0): string {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value)
}

export function formatPercent(value: number, decimals = 1): string {
  return new Intl.NumberFormat('en-US', {
    style: 'percent',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value / 100)
}

// Duration formatting
export function formatDuration(minutes: number): string {
  if (minutes < 60) {
    return `${minutes}m`
  }
  
  const hours = Math.floor(minutes / 60)
  const remainingMinutes = minutes % 60
  
  if (hours < 24) {
    return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`
  }
  
  const days = Math.floor(hours / 24)
  const remainingHours = hours % 24
  
  return remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d`
}

// Health score utilities
export function getHealthStatus(score: number): 'excellent' | 'good' | 'warning' | 'poor' | 'critical' {
  if (score >= 90) return 'excellent'
  if (score >= 75) return 'good'
  if (score >= 50) return 'warning'
  if (score >= 25) return 'poor'
  return 'critical'
}

export function getHealthColor(status: string): string {
  const colors = {
    excellent: 'text-success-green',
    good: 'text-info-blue',
    warning: 'text-warning-amber',
    poor: 'text-orange-600',
    critical: 'text-critical-red'
  }
  return colors[status as keyof typeof colors] || 'text-gray-500'
}

export function getHealthBgColor(status: string): string {
  const colors = {
    excellent: 'bg-emerald-100',
    good: 'bg-blue-100',
    warning: 'bg-amber-100',
    poor: 'bg-orange-100',
    critical: 'bg-red-100'
  }
  return colors[status as keyof typeof colors] || 'bg-gray-100'
}

// Priority utilities
export function getPriorityColor(priority: string): string {
  const colors = {
    critical: 'text-critical-red bg-red-100 border-red-200',
    high: 'text-orange-600 bg-orange-100 border-orange-200',
    warning: 'text-warning-amber bg-amber-100 border-amber-200',
    medium: 'text-warning-amber bg-amber-100 border-amber-200',
    low: 'text-info-blue bg-blue-100 border-blue-200',
    info: 'text-info-blue bg-blue-100 border-blue-200'
  }
  return colors[priority as keyof typeof colors] || 'text-gray-600 bg-gray-100 border-gray-200'
}

// Status utilities
export function getStatusColor(status: string): string {
  const colors = {
    new: 'text-info-blue bg-blue-100 border-blue-200',
    pending: 'text-warning-amber bg-amber-100 border-amber-200',
    'in-progress': 'text-info-blue bg-blue-100 border-blue-200',
    assigned: 'text-info-blue bg-blue-100 border-blue-200',
    completed: 'text-success-green bg-emerald-100 border-emerald-200',
    resolved: 'text-success-green bg-emerald-100 border-emerald-200',
    cancelled: 'text-gray-600 bg-gray-100 border-gray-200',
    overdue: 'text-critical-red bg-red-100 border-red-200',
    acknowledged: 'text-warning-amber bg-amber-100 border-amber-200',
    'work-order-created': 'text-info-blue bg-blue-100 border-blue-200'
  }
  return colors[status as keyof typeof colors] || 'text-gray-600 bg-gray-100 border-gray-200'
}

// Validation utilities
export function validateEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

export function validatePhone(phone: string): boolean {
  const phoneRegex = /^\+?[\d\s\-()]{10,}$/
  return phoneRegex.test(phone)
}

// Array utilities
export function groupBy<T, K extends keyof any>(
  array: T[],
  key: (item: T) => K
): Record<K, T[]> {
  return array.reduce((groups, item) => {
    const group = key(item)
    groups[group] = groups[group] || []
    groups[group].push(item)
    return groups
  }, {} as Record<K, T[]>)
}

export function sortBy<T>(
  array: T[],
  key: keyof T | ((item: T) => any),
  order: 'asc' | 'desc' = 'asc'
): T[] {
  const sortKey = typeof key === 'function' ? key : (item: T) => item[key]
  
  return [...array].sort((a, b) => {
    const aVal = sortKey(a)
    const bVal = sortKey(b)
    
    if (aVal < bVal) return order === 'asc' ? -1 : 1
    if (aVal > bVal) return order === 'asc' ? 1 : -1
    return 0
  })
}

// String utilities
export function truncate(text: string, length: number): string {
  if (text.length <= length) return text
  return text.slice(0, length - 3) + '...'
}

export function capitalizeFirst(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1)
}

export function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^\w-]+/g, '')
    .replace(/--+/g, '-')
    .replace(/^-+/, '')
    .replace(/-+$/, '')
}

// URL utilities
export function buildQueryString(params: Record<string, any>): string {
  const searchParams = new URLSearchParams()
  
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      if (Array.isArray(value)) {
        value.forEach(v => searchParams.append(key, String(v)))
      } else {
        searchParams.append(key, String(value))
      }
    }
  })
  
  return searchParams.toString()
}

// Local storage utilities
export function getStorageItem<T>(key: string, defaultValue: T): T {
  try {
    const item = localStorage.getItem(key)
    return item ? JSON.parse(item) : defaultValue
  } catch {
    return defaultValue
  }
}

export function setStorageItem<T>(key: string, value: T): void {
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch (error) {
    console.warn('Failed to save to localStorage:', error)
  }
}

export function removeStorageItem(key: string): void {
  try {
    localStorage.removeItem(key)
  } catch (error) {
    console.warn('Failed to remove from localStorage:', error)
  }
}

// Debounce utility
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout
  
  return (...args: Parameters<T>) => {
    clearTimeout(timeout)
    timeout = setTimeout(() => func(...args), wait)
  }
}

// Color utilities for charts
export function generateChartColors(count: number): string[] {
  const baseColors = [
    '#2563EB', // trust-blue
    '#0EA5E9', // intelligence-teal  
    '#10B981', // success-green
    '#F59E0B', // warning-amber
    '#DC2626', // critical-red
    '#8B5CF6', // purple
    '#EC4899', // pink
    '#06B6D4', // cyan
  ]
  
  if (count <= baseColors.length) {
    return baseColors.slice(0, count)
  }
  
  // Generate additional colors by varying opacity
  const colors: string[] = []
  for (let i = 0; i < count; i++) {
    const baseColor = baseColors[i % baseColors.length]
    const opacity = 1 - (Math.floor(i / baseColors.length) * 0.2)
    colors.push(baseColor + Math.round(opacity * 255).toString(16).padStart(2, '0'))
  }
  
  return colors
}

// Deep equality check
export function isEqual(a: any, b: any): boolean {
  if (a === b) return true
  
  if (a && b && typeof a === 'object' && typeof b === 'object') {
    const keysA = Object.keys(a)
    const keysB = Object.keys(b)
    
    if (keysA.length !== keysB.length) return false
    
    return keysA.every(key => isEqual(a[key], b[key]))
  }
  
  return false
}

// Additional date utilities needed for dashboard
export function formatDistanceToNow(date: Date): string {
  const now = new Date()
  const diff = now.getTime() - date.getTime()
  
  const minutes = Math.floor(diff / (1000 * 60))
  const hours = Math.floor(diff / (1000 * 60 * 60))
  const days = Math.floor(diff / (1000 * 60 * 60 * 24))
  const weeks = Math.floor(days / 7)
  const months = Math.floor(days / 30)
  
  if (minutes < 1) return 'Just now'
  if (minutes < 60) return `${minutes} minute${minutes !== 1 ? 's' : ''}`
  if (hours < 24) return `${hours} hour${hours !== 1 ? 's' : ''}`
  if (days < 7) return `${days} day${days !== 1 ? 's' : ''}`
  if (weeks < 4) return `${weeks} week${weeks !== 1 ? 's' : ''}`
  return `${months} month${months !== 1 ? 's' : ''}`
}

// Check if a date is overdue
export function isOverdue(dueDate: string): boolean {
  return new Date(dueDate) < new Date()
}